'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  signInAnonymously
} from 'firebase/auth';
import { auth } from '@/lib/firebase/client';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signIn: (e: string, p: string) => Promise<void>;
  signUp: (e: string, p: string, meta?: any) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  checkAuthMethods: (email: string) => Promise<string[]>;
  signInAsGuest: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const signIn = async (e: string, p: string) => {
    await signInWithEmailAndPassword(auth, e, p);
  };

  const signUp = async (e: string, p: string, meta?: any) => {
    // In a real app, you might want to save the user's name (meta.fullName) 
    // to their Firebase user profile or Firestore here.
    await createUserWithEmailAndPassword(auth, e, p);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const checkAuthMethods = async (email: string) => {
    return await fetchSignInMethodsForEmail(auth, email);
  };

  const signInAsGuest = async () => {
    await signInAnonymously(auth);
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signIn,
    signUp,
    signOut,
    resetPassword,
    checkAuthMethods,
    signInAsGuest,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
